import { useCallback, useEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useNavigate } from "@tanstack/react-router";
import { Mic, Square, Loader2, Check, Trash2, Sparkles, MapPin, AlertTriangle, Type, Radio } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { useAnnouncer } from "@/components/ui/live-region";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { DarRecorder, extensionFor, blobToBase64 } from "@/lib/dar-recorder";
import { buzz } from "@/lib/dar-prefs";
import { captureEventLocation, NO_LOCATION, locationStatusLabel } from "@/lib/location-capture";
import { ensureLocationConsent } from "@/lib/location-consent";
import {
  createDarEntry as createDarEntryFn,
  processDarEntry as processDarEntryFn,
  saveGuardComment as saveGuardCommentFn,
  logDarEntry as logDarEntryFn,
  deleteDarEntry as deleteDarEntryFn,
  transcribeVoice as transcribeVoiceFn,
} from "@/lib/dar.functions";
import { enqueueFieldOp, putBlob } from "@/lib/offline-field-queue";
import { flushFieldQueue } from "@/lib/offline-field-sync";
import { setCriticalActivity } from "@/lib/critical-activity";
import { speakDarSaved, DAR_SAVED_QUEUED, DAR_SAVED_SYNCED } from "@/lib/dar-voice-confirm";

export type DarEntry = {
  id: string;
  recorded_at: string;
  audio_duration_sec: number | null;
  transcript_raw: string | null;
  transcript_plain?: string | null;
  summary_ai: string | null;
  summary_source?: string | null;
  guard_comment_text: string | null;
  guard_comment_transcript: string | null;
  comment_type: string;
  quick_tag: string | null;
  status: string;
  flagged: boolean;
  flag_reason: string | null;
  logged_at: string | null;
  locked_at: string | null;
  latitude: number | null;
  longitude: number | null;
  location_status?: string | null;
  location_captured_at?: string | null;
};

type Phase = "idle" | "recording" | "uploading" | "review";

const TAGS: { id: "incident" | "routine" | "delay"; label: string }[] = [
  { id: "incident", label: "Incident" },
  { id: "routine", label: "Routine" },
  { id: "delay", label: "Delay" },
];

export function DarWorkspace({
  open,
  onOpenChange,
  autoStart = false,
  onLogged,
  onPhaseChange,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  autoStart?: boolean;
  onLogged?: () => void;
  /** Presentation-only hook so an external control can mirror capture state. */
  onPhaseChange?: (phase: "idle" | "recording" | "uploading" | "review") => void;
}) {
  const { currentOrg, user } = useAuth();
  const createEntry = useServerFn(createDarEntryFn);
  const processEntry = useServerFn(processDarEntryFn);
  const saveComment = useServerFn(saveGuardCommentFn);
  const logEntry = useServerFn(logDarEntryFn);
  const navigate = useNavigate();
  const deleteEntry = useServerFn(deleteDarEntryFn);
  const transcribe = useServerFn(transcribeVoiceFn);

  const recorderRef = useRef<DarRecorder | null>(null);
  const commentRecorderRef = useRef<DarRecorder | null>(null);
  const [phase, setPhase] = useState<Phase>("idle");
  const [elapsed, setElapsed] = useState(0);
  const [entry, setEntry] = useState<DarEntry | null>(null);
  const [tag, setTag] = useState<"incident" | "routine" | "delay" | null>(null);
  const [comment, setComment] = useState("");
  const [commentMode, setCommentMode] = useState<"none" | "text">("none");
  const [commentRecording, setCommentRecording] = useState(false);
  const [busy, setBusy] = useState(false);
  const { announce, region } = useAnnouncer();
  const reviewRef = useRef<HTMLDivElement | null>(null);

  const reset = useCallback(() => {
    setPhase("idle");
    setElapsed(0);
    setEntry(null);
    setTag(null);
    setComment("");
    setCommentMode("none");
  }, []);

  // Elapsed timer while recording.
  useEffect(() => {
    if (phase !== "recording") return;
    const t = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(t);
  }, [phase]);

  const startRecording = useCallback(async () => {
    if (!currentOrg || !user) {
      toast.error("Select an organization first");
      return;
    }
    try {
      const rec = new DarRecorder();
      await rec.start();
      recorderRef.current = rec;
      setElapsed(0);
      setPhase("recording");
      buzz(15);
    } catch (e: any) {
      toast.error(e?.message ?? "Microphone unavailable");
    }
  }, [currentOrg, user]);

  const stopAndProcess = useCallback(async () => {
    const rec = recorderRef.current;
    if (!rec || !currentOrg || !user) return;
    setPhase("uploading");
    buzz(10);
    try {
      // Stop the microphone first: the location disclosure can block on user
      // input, and nothing said during that prompt belongs in the memo.
      const rr = await rec.stop();
      // Location is captured only at the moment the entry is filed, and only
      // after the user has acknowledged the location disclosure.
      const consented = await ensureLocationConsent(currentOrg.id);
      const loc = consented ? await captureEventLocation() : NO_LOCATION;
      recorderRef.current = null;
      if (rr.blob.size < 1024) {
        toast.error("That recording was empty — try again.");
        setPhase("idle");
        return;
      }
      const id = crypto.randomUUID();
      const ext = extensionFor(rr.mimeType);

      // Persist the original audio on the device first. If the officer is out
      // of coverage — or the upload dies mid-way — the recording survives a
      // reload and syncs later instead of being lost.
      const queueMemo = async () => {
        const blobRef = await putBlob(id, 0, new File([rr.blob], `${id}.${ext}`, { type: rr.mimeType }));
        await enqueueFieldOp({
          id,
          kind: "dar_memo",
          occurredAt: rr.startedAt,
          orgId: currentOrg.id,
          userId: user.id,
          blobs: [blobRef],
          payload: {
            mimeType: rr.mimeType,
            durationSec: rr.durationSec,
            latitude: loc.latitude,
            longitude: loc.longitude,
            accuracyM: loc.accuracyM,
            locationStatus: loc.status,
            locationCapturedAt: loc.capturedAt,
          },
        });
      };

      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        await queueMemo();
        toast.success("Saved on your device — it will sync when you're back online.");
        speakDarSaved(DAR_SAVED_QUEUED, id);
        setPhase("idle");
        return;
      }

      const path = `${user.id}/${id}.${ext}`;
      const up = await supabase.storage.from("dar-audio").upload(path, rr.blob, {
        contentType: rr.mimeType,
        upsert: true,
      });
      if (up.error) {
        await queueMemo();
        toast.success("Saved on your device — it will sync automatically.");
        speakDarSaved(DAR_SAVED_QUEUED, id);
        setPhase("idle");
        void flushFieldQueue();
        return;
      }

      const created = (await createEntry({
        data: {
          orgId: currentOrg.id,
          audioPath: path,
          clientId: id,
          durationSec: rr.durationSec,
          recordedAt: rr.startedAt,
          latitude: loc.latitude,
          longitude: loc.longitude,
          accuracyM: loc.accuracyM,
          locationStatus: loc.status,
          locationCapturedAt: loc.capturedAt,
        },
      })) as DarEntry;
      setEntry(created);
      setPhase("review");

      const processed = (await processEntry({ data: { entryId: created.id } })) as DarEntry;
      setEntry(processed);
      if (processed.flagged) buzz(40);
    } catch (e: any) {
      toast.error(e?.message ?? "Could not save that memo");
      setPhase(entry ? "review" : "idle");
    }
  }, [createEntry, currentOrg, entry, processEntry, user]);

  const recordComment = useCallback(async () => {
    if (!entry) return;
    if (commentRecording) {
      const rec = commentRecorderRef.current;
      commentRecorderRef.current = null;
      setCommentRecording(false);
      if (!rec) return;
      setBusy(true);
      try {
        const rr = await rec.stop();
        const base64 = await blobToBase64(rr.blob);
        const { text } = (await transcribe({ data: { audioBase64: base64, mimeType: rr.mimeType } })) as { text: string };
        if (!text) throw new Error("Nothing was picked up");
        await saveComment({ data: { entryId: entry.id, commentType: "voice", transcript: text } });
        setEntry({ ...entry, comment_type: "voice", guard_comment_transcript: text });
        toast.success("Voice note added");
      } catch (e: any) {
        toast.error(e?.message ?? "Voice note failed");
      } finally {
        setBusy(false);
      }
      return;
    }
    try {
      const rec = new DarRecorder();
      await rec.start();
      commentRecorderRef.current = rec;
      setCommentRecording(true);
      buzz(12);
    } catch (e: any) {
      toast.error(e?.message ?? "Microphone unavailable");
    }
  }, [comment, commentRecording, entry, saveComment, transcribe]);

  const saveTextComment = useCallback(async () => {
    if (!entry || !comment.trim()) return;
    setBusy(true);
    try {
      await saveComment({ data: { entryId: entry.id, commentType: "text", text: comment.trim() } });
      setEntry({ ...entry, comment_type: "text", guard_comment_text: comment.trim() });
      toast.success("Note added");
      setCommentMode("none");
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }, [comment, entry, saveComment]);

  const logIt = useCallback(async () => {
    if (!entry) return;
    setBusy(true);
    try {
      // The guard's local shift date decides which running sheet this lands on.
      const shiftDate = new Date(
        Date.now() - new Date().getTimezoneOffset() * 60000,
      )
        .toISOString()
        .slice(0, 10);
      await logEntry({ data: { entryId: entry.id, quickTag: tag, shiftDate } });
      buzz(20);
      toast.success("DAR successfully added to your D.A.R. Sheet.");
      speakDarSaved(DAR_SAVED_SYNCED, entry.id);
      try {
        sessionStorage.setItem("dar:justAdded", entry.id);
      } catch {
        /* highlighting is cosmetic */
      }
      onLogged?.();
      reset();
      onOpenChange(false);
      void navigate({ to: "/dar" });
    } catch (e: any) {
      toast.error(e?.message ?? "Failed");
    } finally {
      setBusy(false);
    }
  }, [entry, logEntry, navigate, onLogged, onOpenChange, reset, tag]);


  const discard = useCallback(async () => {
    if (!entry) {
      reset();
      return;
    }
    setBusy(true);
    try {
      await deleteEntry({ data: { entryId: entry.id } });
      toast.success("Discarded");
    } catch {
      /* keep going — the entry stays unlogged */
    } finally {
      setBusy(false);
      reset();
    }
  }, [deleteEntry, entry, reset]);

  // Auto-start when the orb was long-pressed — once per open. Without the
  // guard, returning to "idle" while the sheet stays open (discard, empty
  // recording, or a failed save) would immediately start recording again.
  const autoStartedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      autoStartedRef.current = false;
      recorderRef.current?.cancel();
      recorderRef.current = null;
      return;
    }
    if (autoStart && !autoStartedRef.current && phase === "idle") {
      autoStartedRef.current = true;
      void startRecording();
    }
  }, [autoStart, open, phase, startRecording]);

  // Presentation-only: report capture state to an external trigger (nav mic).
  useEffect(() => {
    onPhaseChange?.(open ? phase : "idle");
  }, [open, phase, onPhaseChange]);

  // While a memo is being recorded or saved, non-essential notices (such as the
  // "new version available" banner) stay hidden. Recording itself is untouched.
  useEffect(() => {
    const busy = open && (phase === "recording" || phase === "uploading");
    setCriticalActivity("dar-capture", busy);
    return () => setCriticalActivity("dar-capture", false);
  }, [open, phase]);

  // Announce each stage so a screen-reader user hears the same state the
  // visual design shows, and move focus into the review card when it appears.
  useEffect(() => {
    if (!open) return;
    if (phase === "recording") announce("Recording started. Speak your observation.");
    if (phase === "uploading") announce("Saving your recording.");
    if (phase === "review") {
      announce("Recording saved. Review your entry.");
      reviewRef.current?.focus();
    }
  }, [announce, open, phase]);

  // Transcription finishing is the state guards wait on before logging.
  useEffect(() => {
    if (phase === "review" && entry?.summary_ai) {
      announce("Transcript and summary ready. You can log this entry.");
    }
  }, [announce, entry?.summary_ai, phase]);

  const mmss = `${String(Math.floor(elapsed / 60)).padStart(2, "0")}:${String(elapsed % 60).padStart(2, "0")}`;

  return (
    <Sheet open={open} onOpenChange={(v) => { if (!v) recorderRef.current?.cancel(); onOpenChange(v); }}>
      <SheetContent side="bottom" className="max-h-[92vh] overflow-y-auto rounded-t-2xl px-4 pb-8">
        <SheetHeader className="px-0">
          <SheetTitle className="flex items-center gap-2 text-left">
            <Radio className="h-4 w-4 text-primary" aria-hidden="true" /> Rapid entry
          </SheetTitle>
          <SheetDescription className="sr-only">
            Record a voice occurrence, review the transcript and summary, then log it to your
            D.A.R. Sheet.
          </SheetDescription>
        </SheetHeader>
        {region}

        {/* Capture */}
        {phase !== "review" && (
          <div className="flex flex-col items-center gap-5 py-6">
            <p className="text-center text-sm text-muted-foreground">
              {phase === "recording"
                ? "Speak your observation. Tap stop when you're done."
                : "Tap the mic and speak. We'll write the entry for you."}
            </p>
            {/* Explicit, always-visible capture disclosure while the mic is hot.
                Presentational only — it never touches recording, transcription,
                or storage. The live region above already announces phase
                changes, so this banner is aria-hidden-free but not a duplicate
                announcement source (role="status" + aria-live="off"). */}
            {phase === "recording" && (
              <p
                role="status"
                aria-live="off"
                className="flex items-center justify-center gap-2 rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide text-destructive"
              >
                <span
                  className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-destructive"
                  aria-hidden="true"
                />
                Recording in progress — MissionReady is capturing audio for this D.A.R. entry.
              </p>
            )}
            <button
              type="button"
              disabled={phase === "uploading"}
              onClick={() => (phase === "recording" ? void stopAndProcess() : void startRecording())}
              className={`flex h-32 w-32 items-center justify-center rounded-full text-white shadow-lg transition-transform active:scale-95 disabled:opacity-70 ${
                phase === "recording" ? "animate-pulse bg-destructive" : "bg-primary"
              }`}
              aria-label={phase === "recording" ? "Stop recording" : "Start recording"}
            >
              {phase === "uploading" ? (
                <Loader2 className="h-12 w-12 animate-spin" />
              ) : phase === "recording" ? (
                <Square className="h-12 w-12" />
              ) : (
                <Mic className="h-12 w-12" />
              )}
            </button>
            {/* The ticking clock is decorative for assistive tech: announcing it
                every second would drown out real status changes, which the live
                region below reports instead. */}
            <div className="font-mono text-2xl tabular-nums" aria-hidden="true">
              {phase === "uploading" ? "Saving…" : mmss}
            </div>
          </div>
        )}

        {/* Occurrence card */}
        {phase === "review" && entry && (
          <div
            ref={reviewRef}
            tabIndex={-1}
            role="group"
            aria-label="Review your entry before logging it"
            className="space-y-4 py-4 focus:outline-none"
          >
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <span>{new Date(entry.recorded_at).toLocaleTimeString()}</span>
                {entry.audio_duration_sec ? <span>· {Math.round(entry.audio_duration_sec)}s</span> : null}
                {(entry.latitude != null || entry.location_status) && (
                  <span className="inline-flex items-center gap-1">
                    <MapPin className="h-3 w-3" />
                    {locationStatusLabel(entry.location_status ?? (entry.latitude != null ? "fresh" : null))}
                    {entry.location_status === "stale" && entry.location_captured_at
                      ? ` (${new Date(entry.location_captured_at).toLocaleTimeString()})`
                      : ""}
                  </span>
                )}
              </div>

              {entry.flagged && (
                <div
                  role="alert"
                  className="mt-3 flex items-start gap-2 rounded-lg border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive"
                >
                  <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                  <span>Flagged for supervisor review — {entry.flag_reason}</span>
                </div>
              )}

              {/* AI-generated block, visually distinct from guard-authored content */}
              <div className="mt-3 rounded-lg border border-primary/30 bg-primary/5 p-3">
                <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                  <Sparkles className="h-3 w-3" aria-hidden="true" /> AI summary
                </div>
                <p className="mt-1.5 text-sm leading-relaxed">
                  {entry.summary_ai ?? (
                    <span role="status" className="inline-flex items-center gap-2 text-muted-foreground">
                      <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" /> Transcribing…
                    </span>
                  )}
                </p>
              </div>

              {entry.transcript_raw && (
                <details className="mt-3 text-xs text-muted-foreground">
                  <summary className="cursor-pointer">Verbatim transcript</summary>
                  <pre className="mt-2 whitespace-pre-wrap font-sans text-xs">{entry.transcript_raw}</pre>
                </details>
              )}

              {/* Guard-authored block */}
              {(entry.guard_comment_text || entry.guard_comment_transcript) && (
                <div className="mt-3 rounded-lg border-l-4 border-l-foreground/40 bg-muted/40 p-3">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-foreground/70">
                    Your note
                  </div>
                  <p className="mt-1 text-sm">{entry.guard_comment_text ?? entry.guard_comment_transcript}</p>
                </div>
              )}
            </div>

            {/* Quick tag */}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Quick tag">
              {TAGS.map((t) => (
                <button
                  key={t.id}
                  type="button"
                  aria-pressed={tag === t.id}
                  onClick={() => setTag(tag === t.id ? null : t.id)}
                  className={`rounded-full border px-4 py-2 text-sm ${
                    tag === t.id ? "border-primary bg-primary/15 text-primary" : "border-border"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Add note */}
            {commentMode === "text" ? (
              <div className="space-y-2">
                <Textarea
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  aria-label="Your note (kept separate from the AI summary)"
                  placeholder="Add your own note (kept separate from the AI summary)"
                  rows={3}
                />
                <div className="flex gap-2">
                  <Button size="sm" onClick={saveTextComment} disabled={busy || !comment.trim()}>Save note</Button>
                  <Button size="sm" variant="outline" onClick={() => setCommentMode("none")}>Cancel</Button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" className="flex-1" onClick={() => setCommentMode("text")}>
                  <Type className="mr-1 h-4 w-4" /> Type note
                </Button>
                <Button
                  variant={commentRecording ? "destructive" : "outline"}
                  className="flex-1"
                  disabled={busy}
                  onClick={() => void recordComment()}
                >
                  {commentRecording ? <Square className="mr-1 h-4 w-4" /> : <Mic className="mr-1 h-4 w-4" />}
                  {commentRecording ? "Stop note" : "Voice note"}
                </Button>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                className="h-14 flex-1 text-base"
                disabled={busy || !entry.summary_ai}
                aria-describedby={entry.summary_ai ? undefined : "dar-log-disabled-hint"}
                onClick={() => void logIt()}
              >
                <Check className="mr-2 h-5 w-5" aria-hidden="true" /> Log it
              </Button>
              <Button variant="outline" className="h-14" disabled={busy} onClick={() => void discard()} aria-label="Discard this entry">
                <Trash2 className="h-5 w-5" aria-hidden="true" />
              </Button>
            </div>
            {!entry.summary_ai && (
              <p id="dar-log-disabled-hint" className="sr-only">
                Log it becomes available once the transcript and summary are ready.
              </p>
            )}
            <Button variant="ghost" className="w-full" disabled={busy} onClick={reset}>
              Record another instead
            </Button>
          </div>
        )}

        {entry?.flagged && <Badge variant="destructive" className="sr-only">flagged</Badge>}
      </SheetContent>
    </Sheet>
  );
}
